import { Box, Typography, useTheme } from '@mui/material';
import { forwardRef } from 'react';
import { HeroV2Body, HeroV2Heading, HeroV2ButtonsCollection, ContentTypeRichTextContent, TermsAndConditions as TTermsAndConditions, SmallScreenContent, HeroV2PromoHeadline, HeroV2NestedContentCollection } from '@/types/generated';
import { resolveEyebrowTextColor, resolveTACColor } from '../HeroContent.helpers';
import { TACWrapperStyles, getBodyContainerStyles, getInnerWrapperStyles, getHeroButtonWrapperStyles, heroHeadingStyles, getPromoHeadlineSizeVariant, getHeroHeadingVariant, outerWrapperStyles } from '../HeroContent.styles';
import { getNestedContentHeroWrapperStyles, resolveEyebrowTypographyVariant, getEyebrowStyles, getBodyDocPropsVariant } from './NestedContent.styles';
import ContentfulButton from '@shared-components/components/ContentfulButton';
import ContentTypeRichText from '@shared-components/components/ContentTypeRichText';
import Quiz, { IQuiz } from '@shared-components/components/Quiz';
import TermsAndConditions from '@shared-components/components/TermsAndConditions';
import { Maybe, THeroContentWidth } from '@shared-components/types';
interface INestedContent {
  backgroundColorSmallScreen?: Maybe<string>;
  body?: Maybe<HeroV2Body>;
  bodySize?: Maybe<string>;
  buttons?: HeroV2ButtonsCollection['items'];
  contentAlign?: Maybe<string>;
  contentPosition?: Maybe<string>;
  contentWidth?: Maybe<THeroContentWidth>;
  darkTextColor?: Maybe<boolean>;
  eyebrow?: Maybe<string>;
  eyebrowStyle?: Maybe<string>;
  hasVideoBackground: boolean;
  heading?: Maybe<HeroV2Heading>;
  headingSize?: Maybe<string>;
  isSmallScreen: boolean;
  mediaAspectRatioSmallScreen?: Maybe<string>;
  promoHeadline?: Maybe<HeroV2PromoHeadline>;
  promoHeadlineSize?: Maybe<string>;
  termsConditions?: Maybe<TTermsAndConditions>;
  contentSmallScreen?: Maybe<SmallScreenContent>;
  children?: React.ReactNode;
  isNoContent?: boolean;
  nestedContent?: HeroV2NestedContentCollection['items'];
}
type NestedContentItem = NonNullable<HeroV2NestedContentCollection['items'][number]>;
export const nestedContentComponentsObj = {
  Quiz: (item: NestedContentItem, key: string) => <Quiz key={key} {...item as IQuiz} data-testid="nested_quiz" />
};
const NestedContent = forwardRef<HTMLDivElement, INestedContent>(({
  backgroundColorSmallScreen,
  body,
  bodySize,
  buttons,
  contentAlign,
  contentWidth = 'Medium',
  darkTextColor,
  eyebrow,
  eyebrowStyle,
  hasVideoBackground,
  heading,
  headingSize,
  isSmallScreen,
  mediaAspectRatioSmallScreen,
  promoHeadline,
  promoHeadlineSize,
  termsConditions,
  contentSmallScreen,
  children,
  isNoContent,
  nestedContent
}, ref) => {
  const isDarkBackground = !!backgroundColorSmallScreen?.toLowerCase().includes('dark');
  const {
    bodySmallScreen,
    eyebrowSmallScreen,
    headingSmallScreen,
    focalPointImageSmallScreen
  } = new Object(contentSmallScreen) as SmallScreenContent;
  const eyebrowText = isSmallScreen && eyebrowSmallScreen ? eyebrowSmallScreen : eyebrow;
  const headingContent = isSmallScreen && headingSmallScreen ? headingSmallScreen : heading;
  const bodyContent = isSmallScreen && bodySmallScreen ? bodySmallScreen : body;
  const theme = useTheme();
  if (isSmallScreen && isNoContent) {
    return null;
  }
  return <Box data-testid="section_hero_nested_content" sx={{
    ...getNestedContentHeroWrapperStyles({
      focalPointImageSmallScreen,
      backgroundColorSmallScreen,
      theme
    }),
    position: 'relative'
  }}>
				{children}
				<Box sx={isSmallScreen ? null : outerWrapperStyles(contentWidth)} ref={ref} data-testid="hero_content_main_box">
					<Box className="hero-inner-and-nested-content-wrapper" data-testid="hero_inner_and_nested_content_wrapper" sx={{
        display: 'flex',
        flexDirection: {
          xs: 'column',
          md: 'row'
        },
        justifyContent: 'flex-start',
        columnGap: '1rem'
      }}>
						<Box sx={{
          ...getInnerWrapperStyles(isSmallScreen, contentAlign),
          width: {
            xs: 'auto',
            md: '50%'
          }
        }} data-testid="hero_content_inner_box">
							{eyebrow && <Typography sx={getEyebrowStyles(eyebrowStyle || '')} color={resolveEyebrowTextColor({
            isSmallScreen,
            isDarkBackground,
            darkTextColor,
            isPromoEyebrow: eyebrowStyle === 'Promo',
            mediaAspectRatioSmallScreen
          })} data-testid="text_hero_eyebrow" variant={resolveEyebrowTypographyVariant(eyebrowStyle || '')}>
									{eyebrowText}
								</Typography>}
							{promoHeadline && <ContentTypeRichText disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" backgroundColor="transparent" dataTestId="text_promo_headline" centerAlign={isSmallScreen || contentAlign === 'Center'} content={promoHeadline as ContentTypeRichTextContent} containerSx={{
            ...(contentAlign === 'Left' && {
              marginLeft: 0
            })
          }} docProps={{
            color: darkTextColor ? 'text.primary' : 'text.light',
            variant: getPromoHeadlineSizeVariant(promoHeadlineSize as string, isSmallScreen)
          }} />}

							{heading && <ContentTypeRichText disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" backgroundColor="transparent" dataTestId="group_hero_heading" containerSx={{
            ...heroHeadingStyles,
            ...(contentAlign === 'Left' && {
              marginLeft: 0
            })
          }} centerAlign={isSmallScreen || contentAlign === 'Center'} content={headingContent as ContentTypeRichTextContent} docProps={{
            color: darkTextColor ? 'text.primary' : 'text.light',
            variant: getHeroHeadingVariant(headingSize as string, isSmallScreen)
          }} />}

							{body && <ContentTypeRichText disableGutters disablePaddingOn={['Top', 'Bottom']} contentMaxWidth="100%" dataTestId="group_hero_body" backgroundColor="transparent" centerAlign={isSmallScreen || contentAlign === 'Center'} content={bodyContent as ContentTypeRichTextContent} containerSx={{
            ...getBodyContainerStyles(contentAlign || 'center', !!darkTextColor, isSmallScreen),
            ...(contentAlign === 'Left' && {
              marginLeft: 0
            })
          }} docProps={{
            color: darkTextColor ? 'text.secondary' : 'text.light',
            variant: getBodyDocPropsVariant(isSmallScreen, bodySize || '')
          }} />}

							{buttons && buttons.length > 0 && <Box data-testid="group_hero_buttons_wrapper" sx={getHeroButtonWrapperStyles(contentAlign, isSmallScreen)}>
									{buttons.map(button => button ? <ContentfulButton key={button.sys.id} {...button} dataTestId="button_hero_cta" /> : null)}
								</Box>}
						</Box>

						{nestedContent?.length && <Box data-testid="nested_content_wrapper" sx={{
          width: {
            xs: 'auto',
            md: '50%'
          }
        }}>
								{nestedContent.map((item, index) => {
            if (!item) return null;
            const contentType = item.__typename;
            const key = `nested-item-${index}`;
            const NestedComponent = nestedContentComponentsObj[contentType as keyof typeof nestedContentComponentsObj];
            return NestedComponent ? NestedComponent(item, key) : null;
          })}
							</Box>}
					</Box>

					<Box>
						{termsConditions && <Box data-testid="group_hero_terms_and_conditions_wrapper" sx={TACWrapperStyles}>
								<TermsAndConditions {...termsConditions} textColor={resolveTACColor(hasVideoBackground, !!darkTextColor)} />
							</Box>}
					</Box>
				</Box>
			</Box>;
});
NestedContent.displayName = 'NestedContent';
export default NestedContent;